import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import StudioShell from "@/components/StudioShell";
import { toast } from "sonner";

const STAGES = [
  ["Spatial Sentiment Deconstruction","Synthesising emotional markers from the client questionnaire…"],
  ["Image Reference Feature Extraction","Extracting chroma, edge geometry, and material signatures from uploads…"],
  ["Harmonic DNA Clustering","Balancing light orientation with tactile material undertones…"],
  ["Generating 3 Distinct Directions","Crafting architectural monograph concepts…"],
  ["Material & Palette Synthesis","Formulating exact HEX swatches and finishes…"],
  ["Room Mood Board Composition","Composing spatial vignettes room by room…"],
  ["Presentation Slide Compilation","Rendering 16:9 editorial presentation layouts…"],
];

export default function ProjectAnalysis() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [stage, setStage] = useState(0);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    api.get(`/projects/${id}`).then(r => setProject(r.data));
  }, [id]);

  const started = useRef(false);

  const run = useCallback(async () => {
    if (started.current) return;
    started.current = true;
    setRunning(true);
    const timer = setInterval(() => setStage(s => Math.min(s + 1, STAGES.length - 1)), 1800);
    try {
      await api.post(`/projects/${id}/analyze`);
      clearInterval(timer);
      setStage(STAGES.length - 1);
      setTimeout(() => navigate(`/project/${id}/directions`), 600);
    } catch (e) {
      clearInterval(timer);
      toast.error(apiError(e, "Analysis failed"));
      started.current = false;
      setRunning(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (sp.get("start") === "1") run();
  }, [sp, run]);

  return (
    <StudioShell projectContext={project}>
      <div className="px-6 sm:px-12 py-16 max-w-[1000px] mx-auto">
        <div className="eyebrow mb-3">Spatial Reasoning Engine</div>
        <h1 className="editorial-heading text-4xl sm:text-5xl mb-2">Analysing client preferences</h1>
        <p className="text-stone-500 mb-14 max-w-2xl">The system is now reasoning across your client's questionnaire and uploaded references to synthesise a coherent design direction.</p>
        {!running && (
          <div className="mb-10">
            <button onClick={run} data-testid="analysis-run-btn" className="px-6 py-3 bg-black text-white text-sm">Start Analysis</button>
            <button onClick={() => navigate(`/project/${id}/edit`)} data-testid="analysis-edit-btn" className="ml-3 px-6 py-3 border border-stone-300 text-sm hover:border-black">Go back & edit project details</button>
          </div>
        )}
        <div className="space-y-6">
          {STAGES.map(([t, s], i) => (
            <div key={t} className="flex gap-6 items-start" data-testid={`analysis-stage-${i}`}>
              <div className="font-mono-lab text-xs pt-1 w-12">0{i+1}</div>
              <div className="flex-1 pb-6 border-b" style={{ borderColor: "var(--border)" }}>
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full transition-all ${i < stage ? "bg-emerald-700" : i === stage && running ? "bg-amber-500 animate-pulse" : "bg-stone-300"}`} />
                  <div className={`font-serif-editorial text-xl ${i <= stage ? "text-black" : "text-stone-400"}`}>{t}</div>
                </div>
                <div className={`text-sm mt-1 ml-5 ${i === stage && running ? "text-stone-700" : "text-stone-400"}`}>{s}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </StudioShell>
  );
}
